import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { API, environment } from '@core/envs';
import { IDealerByLocation, IDefaultService, IResponseModel, IRevisionHistory, IServicesByDealer, ResponseModel, RevisionModel } from '@core/interface';
import { Observable, tap } from 'rxjs';
import { RevisionService } from '../revision/revision.service';

@Injectable({
  providedIn: 'root'
})
export class ServicesDealerService {
  readonly #http = inject(HttpClient);
  readonly #revisionService = inject(RevisionService);

  // Signals para armazenar separadamente
  readonly offeredServicesSignal = signal<IDefaultService[]>([]);
  readonly offeredServicesObservationSignal = signal<string>('');

  /**
   * Obtém os serviços e o histórico de serviços de um dealer específico para um veículo.
   * TODO: será descontinuado em breve.
   * @param dealerId - ID do dealer.
   * @param country - País onde o dealer está localizado.
   * @param vin - Número de identificação do veículo (VIN).
   * @returns Um Observable contendo a resposta com os serviços e o histórico.
  */
  getServicesAndHistory(dealerId: number, country: string, vin: string): Observable<IResponseModel<IServicesByDealer>> {
    const api = environment.getRoute(API.transformation);
    const url = `${api}/revisions/v2/services`;
    return this.#http.post<IResponseModel<IServicesByDealer>>(url, { vin }, { params: { country, dealer: dealerId } }).pipe(
      tap(data => {
        // Atualiza o signal e o storage de revisões no service de revisions
        this.#revisionService.setRevisionHistory(data.response?.revisionHistory ?? null);

        // Atualiza o signal e o storage de serviços oferecidos
        this.setOfferedServices(data.response?.offeredServices ?? null);
      })
    );
  }

  // Setters para atualizar os signals e o sessionStorage

  setOfferedServices(offeredServices: IDefaultService[] | null): void {
    this.offeredServicesSignal.set(offeredServices ?? []);
    sessionStorage.setItem('offeredServices', JSON.stringify(offeredServices ?? []));
  }

  setOfferedServicesObservation(observation: string): void {
    this.offeredServicesObservationSignal.set(observation);
    sessionStorage.setItem('offeredServicesObservation', observation);
  }
  //

  // Getters para recuperar os dados dos signals ou do sessionStorage
  getOfferedServices(): IDefaultService[] {
    const offeredServices = this.offeredServicesSignal();

    // Se o signal possui valor, retorna imediatamente
    if (offeredServices && offeredServices.length > 0) {
      return offeredServices;
    }

    // Se o signal está vazio, tenta recuperar do sessionStorage
    const servicesFromStorage = sessionStorage.getItem('offeredServices');

    // Se houver dados no sessionStorage, tenta transformar ele em objeto, salvar no signal e retornar
    if (servicesFromStorage) {
      try {
        const servicesObject = JSON.parse(servicesFromStorage);
        this.setOfferedServices(servicesObject);
        return servicesObject;

        // Se a conversão falhar, retorna um array vazio
      } catch {
        return [];
      }
    }
    // Se não houver dados no sessionStorage, retorna um array vazio
    return [];
  }

  getOfferedServicesObservation(): string {
    const observation = this.offeredServicesObservationSignal();

    // Se o signal possui valor, retorna imediatamente
    if (observation) {
      return observation;
    }

    // Se o signal está vazio, tenta recuperar do sessionStorage
    const observationFromStorage = sessionStorage.getItem('offeredServicesObservation');

    // Se houver dados no sessionStorage, salva no signal e retorna
    if (observationFromStorage) {
      this.setOfferedServicesObservation(observationFromStorage);
      return observationFromStorage;
    }

    // Se não houver dados no sessionStorage, retorna uma string vazia
    return '';
  }
  //
}
